// Need to use the React-specific entry point to import createApi
import config from "../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { prepareHeaders } from "./utils";
import { Answer, QuestionGroupWithRelations } from "../../../api/src/types";
import { questionnaireApi } from "./questionnaire";
import { documentsApi } from "./documents";
import { RootState } from "../store";
import type {
  AnswersDisputeGetResponse,
  AnswersWithAttorneyRestrictionsGetResponse,
} from "../../../api/src/users/answers/route";
import { ResolutionsGetResponse } from "../../../api/src/users/answers/resolutions/route";

// Type for Answer with relations included (allows both string and Date for Redux serialization)
type AnswerWithRelations = Omit<Answer, "createdAt" | "updatedAt"> & {
  createdAt: string | Date;
  updatedAt: string | Date;
  answerQuestionOptions: {
    questionOptionId: string;
    answerId: string;
    isDefault: boolean;
    createdAt: string | Date;
    updatedAt: string | Date;
  }[];
};

// Helper to create optimistic answer data
const createOptimisticAnswer = (
  questionId: string,
  userId: string,
  valueTyped: string | null | undefined,
  questionOptionIds: string[] | undefined,
  documentId = "",
): AnswerWithRelations => {
  const tempId = `temp-${Date.now()}`;

  const now = new Date().toISOString();
  return {
    id: tempId,
    questionId,
    userId,
    documentId,
    valueTyped: valueTyped ?? null,
    deletedAt: null,
    createdAt: now,
    updatedAt: now,
    createdById: userId,
    updatedById: userId,
    answerQuestionOptions:
      questionOptionIds?.map((optionId) => ({
        questionOptionId: optionId,
        answerId: tempId,
        isDefault: false,
        createdAt: now,
        updatedAt: now,
      })) ?? [],
  };
};

// Helper to update answer in question groups
const updateAnswerInQuestionGroups = (
  draft: QuestionGroupWithRelations[],
  questionId: string,
  answer: AnswerWithRelations,
): boolean => {
  for (const group of draft) {
    const question = group.questions.find((q) => q.id === questionId);
    if (question) {
      question.answers = [
        answer as Answer & {
          answerQuestionOptions: {
            questionOptionId: string;
            answerId: string;
            isDefault: boolean;
            createdAt: Date;
            updatedAt: Date;
          }[];
        },
      ];
      return true;
    }
  }
  return false;
};

// Helper to parse RTK Query cache key
const parseCacheKey = (
  key: string,
): { endpoint: string | undefined; args: object } | null => {
  const match = /^(\w+)\(({.*})\)$/.exec(key);
  if (!match?.[1] || !match[2]) return null;

  try {
    return {
      endpoint: match[1],
      args: JSON.parse(match[2]) as object,
    };
  } catch {
    return null;
  }
};

export const answersApi = createApi({
  reducerPath: "answersApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: ["answers", "documents", "disputes", "resolutions"],
  endpoints: (builder) => ({
    getAnswer: builder.query<Answer[], { questionId?: string; userId: string }>(
      {
        query: ({ questionId, userId }) => ({
          url: questionId
            ? `/users/${userId}/answers?questionId=${questionId}`
            : `/users/${userId}/answers`,
        }),
        providesTags: ["answers"],
      },
    ),
    createAnswer: builder.mutation<
      Answer,
      {
        questionId: string;
        userId: string;
        valueTyped?: string | null;
        questionOptionIds?: string[];
      }
    >({
      query: ({ questionId, userId, valueTyped, questionOptionIds }) => ({
        url: `/users/${userId}/answers?questionId=${questionId}`,
        method: "POST",
        body: {
          valueTyped: valueTyped ?? null,
          questionOptionIds: questionOptionIds ?? null,
        },
      }),
      async onQueryStarted(
        { questionId, userId, valueTyped, questionOptionIds },
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { dispatch, queryFulfilled, getState },
      ) {
        // Get all cached questionnaire queries
        const state = getState() as RootState;
        const queries = state.questionnaireApi.queries;
        const patchResults: { undo: () => void }[] = [];

        // Create optimistic answer
        const optimisticAnswer = createOptimisticAnswer(
          questionId,
          userId,
          valueTyped,
          questionOptionIds,
          "",
        );

        // Apply optimistic updates to all relevant cached queries
        Object.entries(queries).forEach(
          ([key, query]: [string, { data?: unknown } | undefined]) => {
            if (!query?.data) return;

            const parsed = parseCacheKey(key);
            if (!parsed) return;

            const { endpoint, args } = parsed;

            // Handle document-based queries
            if (endpoint === "getQuestionnaireSectionGroupsByDocumentId") {
              const patchResult = dispatch(
                questionnaireApi.util.updateQueryData(
                  "getQuestionnaireSectionGroupsByDocumentId",
                  args as { docId: string; section: string },
                  (draft: unknown) => {
                    const typedDraft = draft as QuestionGroupWithRelations[];
                    // Create a new answer object with documentId from query args
                    const answerWithDocumentId = {
                      ...optimisticAnswer,
                      documentId: (args as { docId: string }).docId,
                    };
                    updateAnswerInQuestionGroups(
                      typedDraft,
                      questionId,
                      answerWithDocumentId,
                    );
                  },
                ),
              );
              patchResults.push(patchResult);
            }
          },
        );

        try {
          // Wait for server response
          await queryFulfilled;

          // Invalidate tags to trigger refetch with server data
          dispatch(
            questionnaireApi.util.invalidateTags([
              "QuestionGroup",
              "QuestionOptionVisibility",
              "QuestionnaireSections",
            ]),
          );
          dispatch(
            documentsApi.util.invalidateTags([
              "documents",
              "regeneration-status",
            ]),
          );
        } catch (error) {
          // Revert optimistic updates on error
          patchResults.forEach((result) => {
            result.undo();
          });
          console.error("Failed to create answer:", error);
        }
      },
      invalidatesTags: ["answers"],
    }),
    updateAnswer: builder.mutation<
      Answer,
      {
        questionId: string;
        userId: string;
        valueTyped?: string | null;
        questionOptionIds?: string[];
      }
    >({
      query: ({ questionId, userId, valueTyped, questionOptionIds }) => ({
        url: `/users/${userId}/answers?questionId=${questionId}`,
        method: "PATCH",
        body: {
          valueTyped: valueTyped ?? null,
          questionOptionIds: questionOptionIds ?? null,
        },
      }),
      async onQueryStarted(
        { questionId, userId, valueTyped, questionOptionIds },
        // eslint-disable-next-line @typescript-eslint/unbound-method
        { dispatch, queryFulfilled, getState },
      ) {
        // Get all cached questionnaire queries
        const state = getState() as RootState;
        const queries = state.questionnaireApi.queries;
        const patchResults: { undo: () => void }[] = [];

        // Apply optimistic updates to all relevant cached queries
        Object.entries(queries).forEach(
          ([key, query]: [string, { data?: unknown } | undefined]) => {
            if (!query?.data) return;

            const parsed = parseCacheKey(key);
            if (!parsed) return;

            const { endpoint, args } = parsed;

            // Update answer in cached queries
            const updateAnswer = (draft: unknown) => {
              const typedDraft = draft as QuestionGroupWithRelations[];
              for (const group of typedDraft) {
                const question = group.questions.find(
                  (q) => q.id === questionId,
                );
                if (question?.answers[0]) {
                  const answer = question.answers[0] as AnswerWithRelations;
                  // Update answer fields
                  answer.valueTyped = valueTyped ?? null;
                  answer.updatedAt = new Date().toISOString();
                  answer.answerQuestionOptions =
                    questionOptionIds?.map((optionId) => ({
                      questionOptionId: optionId,
                      userId,
                      answerId: answer.id,
                      isDefault: false,
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString(),
                    })) ?? [];
                  return;
                }
              }
            };

            // Handle both endpoint types
            if (
              endpoint === "getQuestionnaireSectionGroupsByUserId" ||
              endpoint === "getQuestionnaireSectionGroupsByDocumentId"
            ) {
              const patchResult = dispatch(
                questionnaireApi.util.updateQueryData(
                  endpoint,
                  args as
                    | { id: string; section: string }
                    | { docId: string; section: string },
                  updateAnswer,
                ),
              );
              patchResults.push(patchResult);
            }
          },
        );

        try {
          // Wait for server response
          await queryFulfilled;

          // Invalidate tags to trigger refetch with server data
          dispatch(
            questionnaireApi.util.invalidateTags([
              "QuestionGroup",
              "QuestionOptionVisibility",
              "QuestionnaireSections",
            ]),
          );
          dispatch(
            documentsApi.util.invalidateTags([
              "documents",
              "regeneration-status",
            ]),
          );
        } catch (error) {
          // Revert optimistic updates on error
          patchResults.forEach((result) => {
            result.undo();
          });
          console.error("Failed to update answer:", error);
        }
      },
      invalidatesTags: ["answers"],
    }),
    getAnswersWithAttorneyRestrictions: builder.query<
      AnswersWithAttorneyRestrictionsGetResponse,
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `/users/${userId}/answers/attorney-restrictions`,
      }),
      providesTags: ["answers"],
    }),
    getDisputes: builder.query<AnswersDisputeGetResponse, { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/${userId}/answers/dispute`,
      }),
      providesTags: ["disputes"],
    }),
    getResolutions: builder.query<
      ResolutionsGetResponse,
      { userId: string; documentId: string }
    >({
      query: ({ userId, documentId }) => ({
        url: `/users/${userId}/documents/${documentId}/resolutions`,
      }),
      providesTags: ["resolutions"],
    }),
  }),
});

export const {
  useGetAnswerQuery,
  useCreateAnswerMutation,
  useUpdateAnswerMutation,
  useGetAnswersWithAttorneyRestrictionsQuery,
  useGetDisputesQuery,
  useGetResolutionsQuery,
} = answersApi;
